import { useState, useRef, useCallback } from "react";

interface BarChartSeries {
  label: string;
  color: string;
  /**
   * One value per label. `undefined` is a real gap — no bar, no label, and nothing in the tooltip.
   *
   * A month we have no rate for is not a month of zero. Substituting 0 draws a bar saying the
   * carrier lost everything, or ranked zeroth, neither of which happened.
   */
  values: (number | undefined)[];
}

interface BarChartProps {
  labels: string[];
  series: BarChartSeries[];
  stacked?: boolean | undefined;
  /** Formatter for Y-axis tick labels (compact). */
  formatValue?: ((v: number) => string) | undefined;
  /** Formatter for tooltip values (full precision). Falls back to formatValue. */
  formatTooltipValue?: ((v: number) => string) | undefined;
  /**
   * Print each bar's value above it, so the chart can be read without hovering.
   *
   * Opt-in: on a dense chart the labels collide, and most callers are happy with the tooltip.
   * Ignored when there is more than one unstacked series — two labels over two adjacent bars
   * can't be told apart, and a single label over a pair would be a number that isn't either bar.
   * Uses `formatValue`, the compact formatter, since these sit in the width of one bar.
   */
  showValues?: boolean | undefined;
  /**
   * Flip the scale so small values sit at the top and bars hang downward.
   *
   * For a metric where lower is better — a price rank, where 1 is cheapest — a taller bar meaning
   * a worse result reads backwards at a glance. Inverted, the good months are the ones near the
   * top, which is what a reader assumes without being told.
   */
  invertY?: boolean | undefined;
  title?: string | undefined;
}

interface HoverState {
  barIndex: number;
  x: number;
  y: number;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function BarChart({
  labels,
  series,
  stacked = false,
  formatValue,
  formatTooltipValue,
  showValues = false,
  invertY = false,
  title,
}: BarChartProps) {
  const presentValues = series.flatMap((s) =>
    s.values.filter((v): v is number => v !== undefined),
  );
  const maxValue =
    presentValues.length === 0
      ? 0
      : stacked
        ? Math.max(
            ...labels.map((_, i) =>
              series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0),
            ),
          )
        : Math.max(...presentValues);

  const yMax = niceMax(maxValue);
  // Top-to-bottom order of the axis labels. Inverted charts count up as they go down.
  const ticks = invertY
    ? generateTicks(yMax).slice().reverse()
    : generateTicks(yMax);
  const fmt = formatValue ?? ((v: number) => v.toString());
  const tooltipFmt =
    formatTooltipValue ?? formatValue ?? ((v: number) => v.toString());

  // Labels over two side-by-side bars are unreadable, so they're only drawn when there is one bar
  // per month — either a single series, or a stack whose total is one number.
  const labelValues = showValues && (stacked || series.length === 1);

  const [hover, setHover] = useState<HoverState | undefined>(undefined);
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const chartRef = useRef<HTMLDivElement>(null);

  const handleBarEnter = useCallback(
    (barIndex: number, e: React.MouseEvent) => {
      const chartRect = chartRef.current?.getBoundingClientRect();
      if (!chartRect) return;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

      // Calculate bar top from data so tooltip aligns with the top of the bar
      const barHeight = stacked
        ? series.reduce((sum, s) => sum + (s.values[barIndex] ?? 0), 0)
        : Math.max(...series.map((s) => s.values[barIndex] ?? 0));
      const barPx = yMax > 0 ? (barHeight / yMax) * 160 : 0;
      const barTop = rect.top + rect.height - barPx - chartRect.top;

      setHover({
        barIndex,
        x: rect.left + rect.width / 2 - chartRect.left,
        y: barTop,
      });
    },
    [series, stacked, yMax],
  );

  const handleBarLeave = useCallback(() => {
    setHover(undefined);
  }, []);

  return (
    <div>
      {title && (
        <div className="mb-3 text-center text-xs font-bold text-foreground">
          {title}
        </div>
      )}
      <div className="flex">
        {/* Y-axis labels */}
        <div
          className="flex shrink-0 flex-col justify-between pr-2"
          style={{ height: 160 }}
        >
          {ticks.map((t) => (
            <div
              key={t}
              className="text-right font-mono text-[9px] leading-none text-content-muted"
            >
              {fmt(t)}
            </div>
          ))}
        </div>

        {/* Chart area */}
        <div className="relative min-w-0 flex-1" ref={chartRef}>
          {/* Grid lines + bars */}
          <div
            className="relative border-b border-l border-foreground/10"
            style={{ height: 160 }}
          >
            {/* Horizontal grid lines */}
            {ticks.map((t) => (
              <div
                key={t}
                className="absolute right-0 left-0 border-t border-foreground/5"
                style={{ bottom: `${(t / yMax) * 100}%` }}
              />
            ))}

            {/* Bars */}
            <div
              className={`absolute inset-0 flex justify-around px-1 ${invertY ? "items-start" : "items-end"}`}
            >
              {labels.map((label, i) => (
                <div
                  key={label}
                  className={`group/bar relative mx-px flex min-w-0 flex-1 justify-center ${invertY ? "items-start" : "items-end"}`}
                  style={{ height: "100%" }}
                  onMouseEnter={(e) => handleBarEnter(i, e)}
                  onMouseLeave={handleBarLeave}
                >
                  {labelValues && valueAt(series, i, stacked) !== undefined && (
                    <div
                      className="pointer-events-none absolute right-0 left-0 text-center font-mono text-[11px] leading-none font-bold text-foreground"
                      style={
                        invertY
                          ? {
                              top:
                                (yMax > 0
                                  ? (totalAt(series, i, stacked) / yMax) * 160
                                  : 0) + 3,
                            }
                          : {
                              bottom:
                                (yMax > 0
                                  ? (totalAt(series, i, stacked) / yMax) * 160
                                  : 0) + 3,
                            }
                      }
                    >
                      {fmt(totalAt(series, i, stacked))}
                    </div>
                  )}
                  {stacked ? (
                    <div
                      className={`flex w-full max-w-5 flex-col-reverse transition-transform duration-150 ${hover?.barIndex === i ? "scale-x-110 scale-y-[1.03]" : ""}`}
                      style={{ transformOrigin: "bottom center" }}
                    >
                      {series.map((s) => {
                        const h =
                          yMax > 0 ? ((s.values[i] ?? 0) / yMax) * 160 : 0;
                        return (
                          <div
                            key={s.label}
                            className="w-full rounded-t-sm"
                            style={{ height: h, backgroundColor: s.color }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className={`flex h-full w-full justify-center gap-px ${invertY ? "items-start" : "items-end"}`}
                    >
                      {series.map((s) => {
                        const h =
                          yMax > 0 ? ((s.values[i] ?? 0) / yMax) * 160 : 0;
                        return (
                          <div
                            key={s.label}
                            className={`min-w-0 flex-1 ${invertY ? "rounded-b-sm" : "rounded-t-sm"} transition-transform duration-150 ${hover?.barIndex === i ? "scale-x-110 scale-y-[1.03]" : ""}`}
                            style={{
                              height: h,
                              maxWidth: 20 / series.length,
                              backgroundColor: s.color,
                              transformOrigin: invertY
                                ? "top center"
                                : "bottom center",
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tooltip */}
          {hover !== undefined && (
            <BarTooltip_
              hover={hover}
              series={series}
              label={labels[hover.barIndex]!}
              stacked={stacked}
              fmt={tooltipFmt}
            />
          )}

          {/* X-axis labels */}
          <div className="flex justify-around pt-1">
            {labels.map((label, i) => (
              <div
                key={label}
                className={`min-w-0 flex-1 truncate text-center text-[9px] font-medium transition-colors duration-150 ${hover?.barIndex === i ? "text-foreground" : "text-content-muted"}`}
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      {series.length > 1 && (
        <div className="mt-2 flex items-center justify-center gap-4">
          {series.map((s) => (
            <div key={s.label} className="flex items-center gap-1.5">
              <div
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-[10px] font-medium text-content">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// What one x-position is worth: the stack's total, or the single series' value.
function totalAt(
  series: BarChartSeries[],
  index: number,
  stacked: boolean,
): number {
  return stacked
    ? series.reduce((sum, s) => sum + (s.values[index] ?? 0), 0)
    : (series[0]?.values[index] ?? 0);
}

// Undefined when there is nothing to show at this x-position, so the label can be omitted rather
// than printing a zero we invented.
function valueAt(
  series: BarChartSeries[],
  index: number,
  stacked: boolean,
): number | undefined {
  if (stacked) {
    const present = series.filter((s) => s.values[index] !== undefined);
    return present.length === 0 ? undefined : totalAt(series, index, stacked);
  }
  return series[0]?.values[index];
}

function BarTooltip_({
  hover,
  series,
  label,
  stacked,
  fmt,
}: {
  hover: HoverState;
  series: BarChartSeries[];
  label: string;
  stacked: boolean;
  fmt: (v: number) => string;
}) {
  const total = stacked
    ? series.reduce((sum, s) => sum + (s.values[hover.barIndex] ?? 0), 0)
    : 0;

  return (
    <div
      className="pointer-events-none absolute z-50 -translate-x-1/2 -translate-y-full rounded-md border border-border bg-card px-2.5 py-1.5 shadow-lg"
      style={{ left: hover.x, top: hover.y - 6 }}
    >
      <div className="mb-1 text-xs font-semibold text-content">{label}</div>
      {series.map((s) => {
        const value = s.values[hover.barIndex];
        return (
          <div
            key={s.label}
            className="flex items-center gap-1.5 text-sm font-medium text-foreground"
          >
            <div
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            {series.length > 1 && (
              <span className="text-content">{s.label}:</span>
            )}
            {/* No figure at all rather than a zero: this month has no value, which
                is not the same as a value of zero. */}
            <span className="font-bold">
              {value === undefined ? "\u2014" : fmt(value)}
            </span>
          </div>
        );
      })}
      {stacked && series.length > 1 && (
        <div className="mt-1 border-t border-foreground/10 pt-1 text-sm font-bold text-foreground">
          Total: {fmt(total)}
        </div>
      )}
    </div>
  );
}

function niceMax(value: number): number {
  if (value <= 0) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(value)));
  const norm = value / mag;
  if (norm <= 1) return mag;
  if (norm <= 2) return 2 * mag;
  if (norm <= 5) return 5 * mag;
  return 10 * mag;
}

function generateTicks(max: number): number[] {
  const count = 5;
  const step = max / (count - 1);
  return Array.from({ length: count }, (_, i) => max - i * step);
}
